<template>
    <div class="submissions-sidebar-list sidebar-list-wrapper">
        <div class="sidebar-filter">
            <input
                class="form-control"
                placeholder="Filter submissions"
                v-model="filter"
                @keyup.enter="submit"
                ref="filter"
            />
        </div>

        <ul class="sidebar-list" v-if="sortedFilteredSubmissions.length > 0">
            <li
                v-for="sub in sortedFilteredSubmissions"
                :class="{ 'light-selected': curSub && sub.userId === curSub.userId }"
                :tabindex="0"
                @keyup.enter="gotoSub(sub)"
                class="sidebar-list-item"
            >
                <a class="sidebar-item name" @click="gotoSub(sub)">
                    <user :user="sub.user" :anonymize-for="assignment" show-title />

                    <small> Latest: {{ sub.formattedCreatedAt }} </small>
                    <small v-if="sub.assignee.isJust()">
                        Assignee: {{ sub.assignee.unsafeCoerce().name }}
                    </small>
                    <small> Grade: {{ formatGrade(sub) }} </small>
                </a>
            </li>
        </ul>
        <span v-else class="sidebar-list no-items-text"> No submissions found </span>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { AssignmentsStore, SubmissionOrderStore, SubmissionsStore } from '@/store';
import FilterSubmissionsManager from '@/utils/FilterSubmissionsManager';

import User from '../User';

export default {
    name: 'submissions-sidebar-list',

    props: {
        data: {
            type: Object,
            default: null,
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const opts = FilterSubmissionsManager.getOptionsFromRoute(this.$route);
        return {
            filter: opts.filter,
            submitTimeout: null,
            curSub: null,
        };
    },

    computed: {
        ...mapGetters({
            userId: 'user/id',
        }),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        assignmentId() {
            return this.$routeParamAsId('assignmentId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        assignment() {
            return AssignmentsStore.getAssignment()(this.assignmentId).extract();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        courseId() {
            return this.$routeParamAsId('courseId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        submissionId() {
            return this.$routeParamAsId('submissionId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        submissions() {
            return SubmissionsStore.getLatestSubmissions()(this.assignmentId).orDefault([]);
        },

        /** @returns The sorted and filtered submissions based on the route query params. */
        sortedFilteredSubmissions() {
            const sorted = SubmissionOrderStore.sortSubmissions()({
                assignmentId: this.assignmentId,
                submissions: this.submissions,
            }).orDefault(this.submissions);
            return new FilterSubmissionsManager(null, this.userId, this.courseId).filter(
                sorted,
                FilterSubmissionsManager.getOptionsFromRoute(this.$route),
            );
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async mounted() {
        this.$root.$on('sidebar::reload', this.reload);

        await this.reload(false);

        await this.$nextTick();
        const activeEl = document.activeElement;
        if (
            !activeEl ||
            !activeEl.matches('input, textarea') ||
            activeEl.closest('.sidebar .submenu')
        ) {
            this.$refs.filter.focus();
        }
    },

    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filter() {
            this.submitDelayed();
        },

        submissionId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            async handler() {
                // We need to reset curSub to null as this isn't the current
                // submission anymore and loading the new one might take some
                // time.
                this.curSub = null;
                this.curSub = await SubmissionsStore.loadSingleSubmission({
                    courseId: this.courseId,
                    assignmentId: this.assignmentId,
                    submissionId: this.submissionId,
                }).then(response => response.unsafeCoerce().cgResult);
            },
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    destroyed() {
        this.$root.$off('sidebar::reload', this.reload);
    },

    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        formatGrade(sub) {
            return sub.formattedGrade.orDefault('-');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getNewRouteQuery() {
            const opts = {
                ...FilterSubmissionsManager.getOptionsFromRoute(this.$route),
                filter: this.filter,
            };
            return {
                ...this.$route.query,
                ...FilterSubmissionsManager.optionsToRouteQuery(opts),
            };
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        submit() {
            if (this.submitTimeout != null) {
                clearTimeout(this.submitTimeout);
                this.submitTimeout = null;
            }

            this.$router.replace({
                query: this.getNewRouteQuery(),
                hash: this.$route.hash,
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        submitDelayed() {
            if (this.submitTimeout != null) {
                clearTimeout(this.submitTimeout);
            }

            this.submitTimeout = setTimeout(this.submit, 200);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async gotoSub(sub) {
            this.submit();
            await this.$nextTick();

            this.$router.push({
                name: 'submission',
                params: {
                    ...this.$route.params,
                    submissionId: sub.id,
                    fileId: undefined,
                },
                query: this.$route.query,
            });
            this.$emit('close-menu');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async reload(force = true) {
            this.$emit('loading');
            await SubmissionsStore.loadSubmissions({
                assignmentId: this.assignmentId,
                courseId: this.courseId,
                force,
            }).then(response => response.unsafeCoerce());

            this.$emit('loaded');
            await this.$nextTick();

            let el = this.$el.querySelector('.sidebar-list .sidebar-list-item.light-selected');
            if (el) {
                for (let i = 0; i < 2; i++) {
                    if (!el.previousSibling) {
                        break;
                    }
                    el = el.previousSibling;
                }
                el.scrollIntoView(true);
            }
        },
    },

    components: {
        User,
    },
};
</script>

<style lang="less" scoped>
a {
    text-decoration: none;
    color: inherit;
    width: 100%;
    align-items: baseline;
    .name-user {
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 1.5em;
        overflow-x: hidden;
    }
}
</style>
